import React, { useEffect, useMemo, useState } from "react";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Field from "../../components/ui/Field";
import Input from "../../components/ui/Input";
import PageHeader from "../../components/ui/PageHeader";
import RichTextEditor from "../../components/ui/RichTextEditor";
import Switch from "../../components/ui/Switch";
import {
    createTextPageRequest,
    getTextPagesRequest,
    updateTextPageRequest,
} from "../../services/api/content.api";
import { TextPageItem } from "../../services/types/content.types";

interface FormState {
    slug: string;
    titleRu: string;
    titleEn: string;
    titleDe: string;
    titleUa: string;
    bodyRu: string;
    bodyEn: string;
    bodyDe: string;
    bodyUa: string;
    isActive: boolean;
    showOnMainSite: boolean;
    showOnUaSite: boolean;
}

const initialFormState: FormState = {
    slug: "",
    titleRu: "",
    titleEn: "",
    titleDe: "",
    titleUa: "",
    bodyRu: "",
    bodyEn: "",
    bodyDe: "",
    bodyUa: "",
    isActive: true,
    showOnMainSite: true,
    showOnUaSite: false,
};

const TextPagesPage: React.FC = () => {
    const [items, setItems] = useState<TextPageItem[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [formState, setFormState] = useState<FormState>(initialFormState);
    const [searchValue, setSearchValue] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [pageError, setPageError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const filteredItems = useMemo(() => {
        const query = searchValue.trim().toLowerCase();

        if (!query) {
            return items;
        }

        return items.filter((item) => {
            return item.slug.toLowerCase().includes(query) || item.title_ru.toLowerCase().includes(query);
        });
    }, [items, searchValue]);

    const loadData = async () => {
        setIsLoading(true);
        setPageError(null);

        try {
            const response = await getTextPagesRequest();
            setItems(response);
        } catch (error) {
            setPageError(error instanceof Error ? error.message : "Не удалось загрузить страницы");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadData();
    }, []);

    const resetForm = () => {
        setSelectedId(null);
        setFormState(initialFormState);
        setFormError(null);
        setSuccessMessage(null);
    };

    const handleSelect = (item: TextPageItem) => {
        setSelectedId(item.id);
        setFormState({
            slug: item.slug,
            titleRu: item.title_ru,
            titleEn: item.title_en,
            titleDe: item.title_de,
            titleUa: item.title_ua ?? "",
            bodyRu: item.body_ru ?? "",
            bodyEn: item.body_en ?? "",
            bodyDe: item.body_de ?? "",
            bodyUa: item.body_ua ?? "",
            isActive: item.is_active,
            showOnMainSite: item.show_on_main_site ?? true,
            showOnUaSite: item.show_on_ua_site ?? false,
        });
        setFormError(null);
        setSuccessMessage(null);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormError(null);
        setSuccessMessage(null);

        if (!formState.slug.trim() || !formState.titleRu.trim()) {
            setFormError("Slug и русский заголовок обязательны.");
            return;
        }

        setIsSaving(true);

        try {
            const payload = {
                slug: formState.slug.trim(),
                title_ru: formState.titleRu.trim(),
                title_en: formState.titleEn.trim(),
                title_de: formState.titleDe.trim(),
                title_ua: formState.titleUa.trim() || null,
                body_ru: formState.bodyRu.trim() || null,
                body_en: formState.bodyEn.trim() || null,
                body_de: formState.bodyDe.trim() || null,
                body_ua: formState.bodyUa.trim() || null,
                is_active: formState.isActive,
                show_on_main_site: formState.showOnMainSite,
                show_on_ua_site: formState.showOnUaSite,
            };

            if (selectedId) {
                const updated = await updateTextPageRequest(selectedId, payload);
                setItems((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
                handleSelect(updated);
                setSuccessMessage("Страница обновлена.");
            } else {
                const created = await createTextPageRequest(payload);
                setItems((prev) => [created, ...prev]);
                handleSelect(created);
                setSuccessMessage("Страница создана.");
            }
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Ошибка сохранения страницы");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="page-content">
            <PageHeader
                title="Текстовые страницы"
                description="Статические страницы сайта с форматируемым body на трёх языках."
                actions={<Button onClick={resetForm}>Новая страница</Button>}
            />

            <section className="content-split">
                <Card className="content-list-card">
                    <div className="section-toolbar">
                        <div>
                            <h3 className="card-title">Список страниц</h3>
                            <p className="card-text">Управление slug, заголовками и body-контентом.</p>
                        </div>

                        <div className="section-toolbar-controls">
                            <Input
                                type="text"
                                placeholder="Поиск по slug или заголовку..."
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                            />
                        </div>
                    </div>

                    {pageError ? (
                        <div className="form-global-error">{pageError}</div>
                    ) : isLoading ? (
                        <div className="table-skeleton">
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="empty-block">
                            <h3 className="empty-block-title">Нет текстовых страниц</h3>
                            <p className="empty-block-text">Создайте первую страницу контента.</p>
                        </div>
                    ) : (
                        <div className="table-wrap">
                            <table className="data-table">
                                <thead>
                                <tr>
                                    <th>Slug</th>
                                    <th>Title RU</th>
                                    <th>Статус</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredItems.map((item) => (
                                    <tr
                                        key={item.id}
                                        className={item.id === selectedId ? "data-table-row-active" : ""}
                                        onClick={() => handleSelect(item)}
                                    >
                                        <td>{item.slug}</td>
                                        <td>
                                            {item.title_ru}
                                            <span className="tour-sites-badges">
                                                {item.show_on_main_site !== false ? (
                                                    <span className="tour-site-badge" title="Показывается на основном сайте">Основной</span>
                                                ) : null}
                                                {item.show_on_ua_site ? (
                                                    <span className="tour-site-badge tour-site-badge-ua" title="Показывается на втором сайте">UA</span>
                                                ) : null}
                                                {item.show_on_main_site === false && !item.show_on_ua_site ? (
                                                    <span className="tour-site-badge tour-site-badge-none">Скрыта на сайтах</span>
                                                ) : null}
                                            </span>
                                        </td>
                                        <td>
                        <span
                            className={`status-badge ${
                                item.is_active ? "status-badge-active" : "status-badge-inactive"
                            }`}
                        >
                          {item.is_active ? "Активна" : "Выключена"}
                        </span>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>

                <Card className="content-form-card">
                    <div className="editor-head">
                        <div>
                            <h3 className="card-title">{selectedId ? "Редактирование страницы" : "Новая страница"}</h3>
                            <p className="card-text">Body теперь отправляется отдельно на RU, EN, DE и UA (украинский — для второго сайта, необязательно).</p>
                        </div>
                    </div>

                    <form className="editor-form" onSubmit={handleSubmit}>
                        <div className="form-grid form-grid-two-columns">
                            <Field label="Slug" htmlFor="text-page-slug">
                                <Input
                                    id="text-page-slug"
                                    value={formState.slug}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, slug: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Заголовок (RU)" htmlFor="text-page-title-ru">
                                <Input
                                    id="text-page-title-ru"
                                    value={formState.titleRu}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, titleRu: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Заголовок (EN)" htmlFor="text-page-title-en">
                                <Input
                                    id="text-page-title-en"
                                    value={formState.titleEn}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, titleEn: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Заголовок (DE)" htmlFor="text-page-title-de">
                                <Input
                                    id="text-page-title-de"
                                    value={formState.titleDe}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, titleDe: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Заголовок (UA)" htmlFor="text-page-title-ua">
                                <Input
                                    id="text-page-title-ua"
                                    value={formState.titleUa}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, titleUa: event.target.value }))
                                    }
                                />
                            </Field>
                        </div>

                        <RichTextEditor
                            label="Body (RU)"
                            value={formState.bodyRu}
                            onChange={(value) =>
                                setFormState((prev) => ({ ...prev, bodyRu: value }))
                            }
                            minHeight={220}
                        />

                        <RichTextEditor
                            label="Body (EN)"
                            value={formState.bodyEn}
                            onChange={(value) =>
                                setFormState((prev) => ({ ...prev, bodyEn: value }))
                            }
                            minHeight={220}
                        />

                        <RichTextEditor
                            label="Body (DE)"
                            value={formState.bodyDe}
                            onChange={(value) =>
                                setFormState((prev) => ({ ...prev, bodyDe: value }))
                            }
                            minHeight={220}
                        />

                        <RichTextEditor
                            label="Body (UA)"
                            value={formState.bodyUa}
                            onChange={(value) =>
                                setFormState((prev) => ({ ...prev, bodyUa: value }))
                            }
                            minHeight={220}
                        />

                        <div className="tour-sites-block">
                            <span className="tour-sites-title">Показывать на сайтах</span>
                            <label className="tour-sites-option">
                                <input
                                    type="checkbox"
                                    checked={formState.showOnMainSite}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, showOnMainSite: event.target.checked }))
                                    }
                                />
                                Основной сайт (RU / EN / DE)
                            </label>
                            <label className="tour-sites-option">
                                <input
                                    type="checkbox"
                                    checked={formState.showOnUaSite}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, showOnUaSite: event.target.checked }))
                                    }
                                />
                                Второй сайт (RU / DE / UA)
                            </label>
                            {!formState.showOnMainSite && !formState.showOnUaSite ? (
                                <span className="tour-sites-hint">
                                    Не отмечен ни один сайт — страница не будет открываться ни на одном сайте.
                                </span>
                            ) : null}
                        </div>

                        <div className="form-switch-row">
                            <Switch
                                checked={formState.isActive}
                                onChange={(checked) =>
                                    setFormState((prev) => ({ ...prev, isActive: checked }))
                                }
                                label={formState.isActive ? "Страница активна" : "Страница выключена"}
                            />
                        </div>

                        {formError ? <div className="form-global-error">{formError}</div> : null}
                        {successMessage ? <div className="form-global-success">{successMessage}</div> : null}

                        <div className="editor-actions">
                            <Button type="submit" disabled={isSaving}>
                                {isSaving ? "Сохраняем..." : selectedId ? "Сохранить изменения" : "Создать страницу"}
                            </Button>
                            <Button type="button" variant="secondary" onClick={resetForm}>
                                Очистить форму
                            </Button>
                        </div>
                    </form>
                </Card>
            </section>
        </div>
    );
};

export default TextPagesPage;
